<template>
    <div>
        <dialog-header v-if="showImage" :image="image" :show-close="showClose" @close="$emit('close')">
            <template #pretitle>
                <booking-status :space="course.space" :space-left="course.spaceLeft" :first-booking-time="course.firstBookingTime" :last-booking-time="course.lastBookingTime" size="22px" :text="true" />
            </template>
        </dialog-header>

        <div :class="textClass">
            <div class="d-flex align-baseline justify-space-between" style="min-width: 0; gap: 8px;">
                <h1 v-if="showTitle" class="course-header__title" :class="{'mt-4': showImage}">{{course.name}}</h1>
                <span
                    v-if="bookingLabelItems && bookingLabelItems.length === 1"
                    class="course-header__booking-label primary--text text-body-2 ml-auto"
                    :class="{'mt-2': showImage}"
                >{{$translate('Booked for {0}').format(bookingLabelItems[0].name)}}</span>

                <v-menu v-else-if="bookingLabelItems && bookingLabelItems.length > 1" offset-y left>
                    <template v-slot:activator="{ on }">
                        <span
                            v-on="on"
                            class="course-header__booking-label course-header__booking-label--expandable primary--text text-body-2 ml-auto"
                            :class="{'mt-2': showImage}"
                        >
                            {{$translate('{0} persons booked').format(bookingLabelItems.length)}}
                            <v-icon small color="primary">mdi-chevron-down</v-icon>
                        </span>
                    </template>
                    <v-list dense>
                        <v-list-item v-for="item in bookingLabelItems" :key="item.id">
                            <v-list-item-title>{{item.name}}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
            <div v-if="course.subtitle" class="course-header__subtitle text--secondary">{{course.subtitle}}</div>

            <div class="course-header__meta text--secondary mt-2">
                <div v-if="course.occasions" class="course-header__meta-item">
                    <v-icon small color="primary" class="mr-1">mdi-calendar-check</v-icon>{{occasionsString}}
                </div>
                <div class="course-header__meta-item">
                    <v-icon small color="primary" class="mr-1">mdi-calendar-range</v-icon>{{courseDate}}
                </div>
                <div v-if="sitesAndResourcesText" class="course-header__meta-item">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker</v-icon>{{sitesAndResourcesText}}
                </div>
                <div class="course-header__meta-item">
                    <template v-if="discountedPrice && discountedPrice.has_discount">
                        <span class="text-decoration-line-through text--secondary" style="font-size: 0.85em">{{discountedPrice.base_price | price}}</span>
                        <v-icon small color="primary" class="ml-1 mr-1">mdi-tag-outline</v-icon>{{discountedPrice.final_price | price}}
                    </template>
                    <template v-else>
                        <v-icon small color="primary" class="mr-1">mdi-tag-outline</v-icon>{{course.price | price}}
                    </template>
                </div>
                <div v-if="categoryName" class="course-header__meta-item">
                    <v-icon small color="primary" class="mr-1">mdi-folder-multiple-outline</v-icon>{{categoryName}}
                </div>
                <div v-if="purchaseAgeText" class="course-header__meta-item">
                    <v-icon small color="primary" class="mr-1">mdi-account-clock-outline</v-icon>{{purchaseAgeText}}
                </div>
            </div>

            <div v-if="showInstructors && formattedStaffs && formattedStaffs.length" class="mt-3">
                <div class="text-subtitle-2 font-weight-bold">{{$translate('Instructors')}}</div>
                <div>
                    <a href v-for="(staff,index) in formattedStaffs" :key="staff.id" @click.prevent="doShowStaffInfoDialog(staff)" class="primary--text z-no-underline">{{staff.displayName}}{{index != formattedStaffs.length-1 ? ' | ' : ''}}</a>
                </div>
            </div>
        </div>
        <staff-info-dialog v-model="showStaffInfoDialog" :staffId="showStaff.id" v-if="showInstructors && showStaff"/>
    </div>
</template>

<script>
import { getName, formatStaffName } from '../util.js'
import BookingStatus from '../components/BookingStatus.vue'
import DialogHeader from '../components/DialogHeader.vue'
import StaffInfoDialog from './StaffInfoDialog.vue'
export default {
    name: 'zoezi-course-header',

    props: {
        course: {},
        showImage: {},
        showTitle: {},
        textClass: {},
        showClose: {},
        showLastnameForInstructors: {
            type: Boolean,
            default: false
        },
        showInstructors: {
            type: Boolean,
            default: true
        },
        bookingLabelItems: {
            type: Array,
            default: null
        },
        discountedPrice: { default: null },
    },

    components: {
        BookingStatus,
        DialogHeader,
        StaffInfoDialog
    },

    data: () => ({
        showStaffInfoDialog: false,
        showStaff: null
    }),

    computed: {
        staffs() {
            if (!this.course) {
                return null;
            }

            if (this.course.staffs) {
                return this.course.staffs;
            }

            if (this.course.workouts.length == 0) {
                return null;
            }

            let staffs = [];
            this.course.workouts.forEach(w => {
                w.staffs.forEach(s => {
                    if (!staffs.find(x => x.id == s.id)) {
                        staffs.push(s);
                    }
                })
            })

            return staffs;
        },
        formattedStaffs() {
            const list = this.staffs || [];
            return list.map(s => ({
                ...s,
                displayName: formatStaffName(s, this.showLastnameForInstructors)
            }));
        },
        resources() {
            if (!this.course) {
                return [];
            }

            if (this.course.resources) {
                return this.course.resources.map(x => x.name);
            }

            let resources = [];
            this.course.workouts.forEach(w => {
                w.resources.forEach(s => {
                    let name = getName(s);
                    if (resources.indexOf(name) == -1) {
                        resources.push(name);
                    }
                })
            })

            return resources;
        },
        category() {
            return this.$store.state.settings?.coursecategories?.find(c => c.id == this.course.category_id);
        },
        image() {
            if (this.course.imagekey) {
                return this.course.imagekey;
            }

            return this.category?.imagekey;
        },
        categoryName() {
            return this.category?.name;
        },
        sites() {
            if (!this.$store.state.settings || !this.$store.state.settings.sites || this.$store.state.settings.sites.length == 1) {
                return [];
            }

            return this.course.sites
                .map(site_id => this.$store.state.settings.sites.find(x => x.id === site_id)?.name)
                .filter(name => typeof name === 'string');
        },
        sitesAndResourcesText() {
            return this.sites.concat(this.resources).join(', ');
        },
        occasionsString() {
            if (this.course.occasions == 1) {
                return this.$translate('1 occasion');
            }
            return this.$translate('{0} occasions').format(this.course.occasions);
        },
        courseDate() {
            if(this.course.startTime.yyyymmdd() === this.course.endTime.yyyymmdd()) {
                return Date.formatTimeFullRelative(this.course.startTime).capitalize() + ' - ' + this.course.endTime.hhmm();
            }
            return Date.formatTimeFullRelative(this.course.startTime).capitalize() + ' - ' + Date.formatTimeFullRelative(this.course.endTime);
        },
        purchaseAgeText() {
            const pa = this.course?.purchase_age;
            if (!pa || !pa.from) return null;
            const to = Number(pa.to);
            if (!to || isNaN(to)) {
                return this.$translate('{0}+ years').format(pa.from);
            }
            return this.$translate('{0}–{1} years').format(pa.from, pa.to);
        }
    },
    methods: {
        doShowStaffInfoDialog(staff) {
            this.showStaffInfoDialog = true;
            this.showStaff = staff;
        }
    }
}
</script>

<style scoped>
.course-header__title {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.2;
    min-width: 0;
    flex-shrink: 1;
}

.course-header__booking-label {
    flex-shrink: 0;
    max-width: 45%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: right;
}

.course-header__booking-label--expandable {
    cursor: pointer;
}


.course-header__subtitle {
    font-size: 0.95rem;
    margin-top: 4px;
    line-height: 1.3;
}

.course-header__meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 0.875rem;
}

.course-header__meta-item {
    display: flex;
    align-items: center;
    white-space: nowrap;
}
</style>
